import React from "react";
import { Button, Input, Space, Tag, Typography } from "antd";
import { AudioOutlined, LoadingOutlined, StopOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Error as ErrorMessage } from "../../../commons/Messages/Messages";
import useDictation from "../measurements/useDictation";
import { transcribeAudio } from "./api";

/**
 * What was done, typed or spoken. Live recognition writes while speaking; where the browser has
 * none (the app, Firefox) the recording is written by the server when it stops. With the
 * "Translate voice input into" preference, the server writes it in that language.
 */
const NotesField = ({ value, onChange, onAppend, voiceConfigured, voiceTranslateTo, disabled }) => {
  const { t, i18n } = useTranslation("dashboard");

  const dictation = useDictation({
    language: i18n.language,
    serverAvailable: voiceConfigured,
    translate: Boolean(voiceTranslateTo),
    transcribe: transcribeAudio,
    onText: onAppend,
    onError: (code, err) => {
      if (code === "server") return generalErrorAlert(err);
      const messages = {
        microphone: "Please allow access to the microphone.",
        empty: "Nothing was understood. Please try again.",
        unsupported: "Voice input is not available on this device.",
        live: "Speech recognition stopped. Please try again."
      };
      ErrorMessage(t(messages[code] || messages.live));
    }
  });

  const active = dictation.status === "listening" || dictation.status === "recording";
  const busy = dictation.status === "processing" || dictation.status === "starting";
  const seconds = Math.floor(dictation.elapsed / 1000);

  return (
    <div className="sd-notes">
      <Input.TextArea
        value={value}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
        autoSize={{ minRows: 5, maxRows: 16 }}
        maxLength={20000}
        placeholder={t(
          "What did you do or find? e.g. old tiles removed in the bathroom, water damage behind the bathtub, pipes sealed."
        )}
      />
      {dictation.status === "listening" && (
        <Typography.Paragraph type="secondary" className="sd-interim">
          <span className="sd-rec-dot" /> {dictation.interim || t("Listening…")}
        </Typography.Paragraph>
      )}
      {dictation.status === "recording" && (
        <Typography.Paragraph type="secondary" className="sd-interim">
          <span className="sd-rec-dot" /> {t("Recording")} {Math.floor(seconds / 60)}:
          {String(seconds % 60).padStart(2, "0")} · {t("The text appears when you stop.")}
        </Typography.Paragraph>
      )}
      {!disabled && dictation.available && (
        <Space wrap className="sd-notes-actions">
          <Button
            size="large"
            shape="round"
            type={active ? "primary" : "default"}
            danger={active}
            className={active ? "sd-mic sd-mic--active" : "sd-mic"}
            icon={busy ? <LoadingOutlined /> : active ? <StopOutlined /> : <AudioOutlined />}
            disabled={busy}
            onClick={active ? dictation.stop : dictation.start}
          >
            {dictation.status === "processing"
              ? t("Writing…")
              : active
                ? t("Stop")
                : t("Speak instead of typing")}
          </Button>
          {voiceTranslateTo && dictation.mode === "recording" && (
            <Tag color="blue">{t("Translated by your voice input preference")}</Tag>
          )}
        </Space>
      )}
    </div>
  );
};

export default NotesField;
